import React, { useState } from 'react';
import { X, AlertCircle, Loader2, Check } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { formatCents } from '../../utils/credits';
import { startCheckout, CheckoutResponse } from '../../lib/paddle';

interface PurchaseCreditsModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const PRESET_AMOUNTS = [10, 25, 50, 100];

export const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({
  show,
  onHide,
  onSuccess
}) => {
  const api = useAuthenticatedApi();
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [saveCard, setSaveCard] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const computedPurchaseAmount = (() => {
    const val = customAmount ? parseFloat(customAmount) : amount;
    return Number.isFinite(val) && val > 0 ? val : 0;
  })();

  const handlePurchase = async () => {
    setLoading(true);
    setError(null);

    try {
      const purchaseAmount = customAmount ? parseFloat(customAmount) : amount;
      
      if (isNaN(purchaseAmount) || purchaseAmount <= 0 || purchaseAmount > 1000) {
        setError('Please enter a valid amount between $1 and $1000');
        setLoading(false);
        return;
      }
      
      const response = await api.post<CheckoutResponse>(apiEndpoints.billing.purchaseCredits, {
        amount: purchaseAmount,
        save_payment_method: saveCard
      });

      // CityPay redirects to the hosted payment page; Paddle opens an overlay and the
      // credits arrive via webhook shortly after checkout completes.
      await startCheckout(api, response, {
        onComplete: () => {
          setLoading(false);
          onHide();
          onSuccess();
        },
      });

      // For overlay checkout the modal stays open behind the overlay until completion;
      // stop the spinner so a dismissed overlay doesn't strand the button.
      if (response.checkout) {
        setLoading(false);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handleAmountClick = (value: number) => {
    setAmount(value);
    setCustomAmount('');
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Purchase Credits
          </h3>
          <button
            onClick={onHide}
            className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-neutral-500 dark:text-neutral-400" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}
        
        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Select Amount
            </p>
            <div className="grid grid-cols-4 gap-2">
              {PRESET_AMOUNTS.map(value => (
                <button
                  key={value}
                  onClick={() => handleAmountClick(value)}
                  className={`py-2 px-3 rounded-lg font-medium transition-colors ${
                    amount === value && !customAmount
                      ? 'bg-brand-primary text-black'
                      : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                  }`}
                >
                  ${value}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Or enter custom amount
            </p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400">
                $
              </span>
              <input
                type="number"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  if (e.target.value) {
                    setAmount(0);
                  }
                }}
                min="1"
                max="1000"
                step="1"
                className="w-full pl-8 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Minimum: $1, Maximum: $1,000
            </p>
          </div>

          {/* Credits preview */}
          <div className="p-3 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg">
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              Credits you'll receive: <span className="font-semibold">{formatCents(computedPurchaseAmount)}</span>
            </div>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-brand-primary bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-600 rounded focus:ring-brand-primary focus:ring-2"
              />
              <span className="text-sm text-neutral-700 dark:text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-white">
                Save payment method for future purchases and auto top-ups
              </span>
            </label>
          </div>

          <div className="space-y-2 p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
              <Check size={16} className="text-green-500 dark:text-green-400" />
              <span>Secure checkout</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
              <Check size={16} className="text-green-500 dark:text-green-400" />
              <span>PCI compliant checkout</span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onHide}
            disabled={loading}
            className="px-4 py-2 text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePurchase}
            disabled={loading || (!amount && !customAmount)}
            className="px-4 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Processing...
              </>
            ) : (
              `Purchase $${customAmount || amount}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}; 